import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { courseService } from '../services/firebase/courses';
import { Course } from '../types/course';
import { UserProgress } from '../types/course';
import jsPDF from 'jspdf';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Download, BookOpen, ArrowLeft, Home } from 'lucide-react';

type DocumentWithFonts = Document & {
  fonts?: {
    ready: Promise<void>;
    status?: string;
  };
};

type ImageAsset = {
  dataUrl: string;
  width: number;
  height: number;
};

type TextBlock = {
  text: string;
  fontSize: number;
  fontStyle?: 'normal' | 'bold';
  color?: [number, number, number];
  lineHeight?: number;
  spaceAfter: number;
};

const fetchImageAsDataUrl = async (src: string): Promise<string | null> => {
  try {
    const response = await fetch(src, { cache: 'force-cache' });
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn(`Unable to load certificate image ${src}:`, error);
    return null;
  }
};

const fetchImageWithDimensions = async (src: string): Promise<ImageAsset | null> => {
  const dataUrl = await fetchImageAsDataUrl(src);
  if (!dataUrl) {
    return null;
  }

  return await new Promise((resolve) => {
    const image = new Image();
    image.onload = () =>
      resolve({
        dataUrl,
        width: image.width,
        height: image.height
      });
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
};

const getScaledDimensions = (image: ImageAsset, maxWidth: number, maxHeight: number) => {
  const widthScale = maxWidth / image.width;
  const heightScale = maxHeight / image.height;
  const scale = Math.min(widthScale, heightScale, 1);

  return {
    width: image.width * scale,
    height: image.height * scale
  };
};

export const CertificatePage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    const fetchCertificateData = async () => {
      if (!courseId || !user) return;
      try {
        setLoading(true);
        const courseData = await courseService.getCourse(courseId);
        const progressData = await courseService.getUserProgress(courseId);
        setCourse(courseData);
        setUserProgress(progressData);
      } catch (error) {
        console.error('Error fetching certificate data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificateData();
  }, [courseId, user]);

  useEffect(() => {
    if (!course || !user || !userProgress?.completedAt) {
      return;
    }

    let isMounted = true;

    const waitForFontsToLoad = async () => {
      const docWithFonts = document as DocumentWithFonts;
      if (docWithFonts.fonts && docWithFonts.fonts.status !== 'loaded') {
        try {
          await docWithFonts.fonts.ready;
        } catch {
          // Ignore font loading errors and continue with default fonts.
        }
      }
    };

    const generatePdfPreview = async () => {
      setIsGeneratingPdf(true);

      try {
        await waitForFontsToLoad();
        const pdf = new jsPDF('landscape', 'pt', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const innerMargin = 40;

        pdf.setFillColor(245, 245, 245);
        pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(
          innerMargin,
          innerMargin,
          pdfWidth - innerMargin * 2,
          pdfHeight - innerMargin * 2,
          24,
          24,
          'F'
        );

        const centerX = pdfWidth / 2;
        const [tuLogo, powerLogo, authenticityBadge] = await Promise.all([
          fetchImageWithDimensions('/tulogo.png'),
          fetchImageWithDimensions('/U to the power of AI.png'),
          fetchImageWithDimensions('/Stamp of authenticity.png')
        ]);

        const safeTextWidth = pdfWidth - innerMargin * 2 - 160;
        const measureTextLines = (text: string, fontSize: number, fontStyle: 'normal' | 'bold' = 'normal') => {
          pdf.setFont('helvetica', fontStyle);
          pdf.setFontSize(fontSize);
          return pdf.splitTextToSize(text, safeTextWidth);
        };

        const logoMaxWidth = Math.max((centerX - innerMargin - 110), 200);
        const logoMaxHeight = 110;

        const tuLogoDimensions = tuLogo
          ? getScaledDimensions(tuLogo, logoMaxWidth, logoMaxHeight)
          : null;
        if (tuLogo && tuLogoDimensions) {
          pdf.addImage(
            tuLogo.dataUrl,
            'PNG',
            innerMargin + 40,
            innerMargin + 30,
            tuLogoDimensions.width,
            tuLogoDimensions.height,
            undefined,
            'FAST'
          );
        }

        const powerLogoDimensions = powerLogo
          ? getScaledDimensions(powerLogo, logoMaxWidth, logoMaxHeight)
          : null;
        if (powerLogo && powerLogoDimensions) {
          const x = pdfWidth - innerMargin - 40 - powerLogoDimensions.width;
          pdf.addImage(
            powerLogo.dataUrl,
            'PNG',
            x,
            innerMargin + 30,
            powerLogoDimensions.width,
            powerLogoDimensions.height,
            undefined,
            'FAST'
          );
        }

        const participantName = user?.displayName?.trim() || user?.email || 'Participant';
        const completionDate =
          userProgress?.completedAt
            ?.toDate()
            .toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) || '';

        const textBlockDefinitions: TextBlock[] = [
          {
            text: 'Certificate of Completion',
            fontSize: 36,
            fontStyle: 'bold',
            color: [20, 90, 140],
            lineHeight: 38,
            spaceAfter: 40
          },
          {
            text: 'This certifies that',
            fontSize: 16,
            color: [80, 80, 80],
            lineHeight: 20,
            spaceAfter: 40
          },
          {
            text: participantName,
            fontSize: 28,
            fontStyle: 'bold',
            color: [30, 30, 30],
            lineHeight: 34,
            spaceAfter: 40
          },
          {
            text: 'has successfully completed the course',
            fontSize: 16,
            color: [80, 80, 80],
            lineHeight: 20,
            spaceAfter: 40
          },
          {
            text: course.title,
            fontSize: 24,
            fontStyle: 'bold',
            color: [30, 30, 30],
            lineHeight: 30,
            spaceAfter: 40
          },
          {
            text: `on ${completionDate}`,
            fontSize: 16,
            color: [80, 80, 80],
            lineHeight: 20,
            spaceAfter: 50
          }
        ];

        const textBlocks: Array<TextBlock & { lines: string[] }> = textBlockDefinitions.map((block) => ({
          ...block,
          lines: measureTextLines(block.text, block.fontSize, block.fontStyle ?? 'normal')
        }));

          // We'll determine cursorStart dynamically to better center text + badge combined.
          let cursorStart = innerMargin + 0; // temporary default; recalculated later.
  
          // Use a fixed, device-independent badge height so it renders identically across devices
          const badgeTargetHeight = 100; // points (A4 landscape height is ~595pt)
  
          // Scale badge to target height while preserving aspect ratio
          let badgeDimensions = authenticityBadge
            ? {
                width: authenticityBadge.width * (badgeTargetHeight / authenticityBadge.height),
                height: badgeTargetHeight,
              }
            : null;
  
          // Compute desired gap between last text line and the badge equal to a line-height
          const lastBlock = textBlocks[textBlocks.length - 1];        const lastBlockLineHeight = lastBlock ? (lastBlock.lineHeight ?? lastBlock.fontSize * 1.2) : 20;
        const desiredGapBetweenTextAndBadge = lastBlockLineHeight;

        // Compute total height of text blocks but replace the last block's spaceAfter with desiredGapBetweenTextAndBadge
        const textBlocksHeight = textBlocks.reduce((sum, block, idx) => {
          const effectiveLineHeight = block.lineHeight ?? block.fontSize * 1.2;
          const spaceAfter = idx === textBlocks.length - 1 ? desiredGapBetweenTextAndBadge : block.spaceAfter;
          return sum + (block.lines.length - 1) * effectiveLineHeight + spaceAfter;
        }, 0);

        // Vertical area: start just below logos with some padding, end above bottom margin.
  // True vertical centering within inner rectangle with symmetric breathing room.
  const topPadding = 120; // breathing room from top inner edge
  const bottomPadding = 60; // breathing room from bottom inner edge
  const topSafeY = innerMargin + topPadding;
  const bottomSafeY = pdfHeight - innerMargin - bottomPadding;

  // combined height: text blocks (already includes gap) + badge height
  const combinedHeight = textBlocksHeight + (badgeDimensions ? badgeDimensions.height : 0);
        const availableHeight = bottomSafeY - topSafeY;

        // Center combined group within available height, fallback to topSafeY
  // Initial center position (baseline of first line)
  const overflow = combinedHeight - availableHeight;
  const initialStart = overflow > 0 ? topSafeY : topSafeY + (availableHeight - combinedHeight) / 2;

  // Adjust to equalize top and bottom gaps accounting for baseline vs visual box difference.
  const topSpace = initialStart - topSafeY;
  const bottomSpace = availableHeight - topSpace - combinedHeight;
  // Shift by half the difference so gaps become equal (clamped in bounds)
  const adjustStart = (bottomSpace - topSpace) / 2;
  let centeredStart = initialStart + adjustStart;
  // Clamp within allowed vertical range
  centeredStart = Math.max(topSafeY, Math.min(topSafeY + availableHeight - combinedHeight, centeredStart));
        cursorStart = centeredStart;

        let cursorY = cursorStart;

        textBlocks.forEach((block, idx) => {
          const { fontSize, fontStyle = 'normal', color = [30, 30, 30] } = block;
          const effectiveLineHeight = block.lineHeight ?? fontSize * 1.2;
          pdf.setFont('helvetica', fontStyle);
          pdf.setFontSize(fontSize);
          pdf.setTextColor(...color);

          block.lines.forEach((line: string, index: number) => {
            pdf.text(line, centerX, cursorY + index * effectiveLineHeight, { align: 'center' });
          });

          const spaceAfter = idx === textBlocks.length - 1 ? desiredGapBetweenTextAndBadge : block.spaceAfter;
          cursorY += (block.lines.length - 1) * effectiveLineHeight + spaceAfter;
        });

        if (authenticityBadge && badgeDimensions && badgeDimensions.height > 0) {
          const badgeX = centerX - badgeDimensions.width / 2;
          const badgeY = cursorY;
          pdf.addImage(
            authenticityBadge.dataUrl,
            'PNG',
            badgeX,
            badgeY,
            badgeDimensions.width,
            badgeDimensions.height,
            undefined,
            'FAST'
          );
        }

        const blob = pdf.output('blob');
        const url = URL.createObjectURL(blob);

        if (isMounted) {
          setPdfUrl((prevUrl) => {
            if (prevUrl) {
              URL.revokeObjectURL(prevUrl);
            }
            return url;
          });
        } else {
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Error generating certificate PDF:', error);
      } finally {
        if (isMounted) {
          setIsGeneratingPdf(false);
        }
      }
    };

    generatePdfPreview();

    return () => {
      isMounted = false;
    };
  }, [course, user, userProgress]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleDownload = () => {
    if (!pdfUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = pdfUrl;
    const safeCourseTitle = course?.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'certificate';
    link.download = `${safeCourseTitle}-trainedup.pdf`;
    link.click();
  };

  if (loading) {
    return <Layout><div className="text-center py-12">Loading certificate...</div></Layout>;
  }

  if (!course || !user || !userProgress || !userProgress.completedAt) {
    return <Layout><div className="text-center py-12">Certificate not available.</div></Layout>;
  }

  return (
    <Layout>
      <div className="bg-neutral-100 py-8 px-4 min-h-screen">
        <div className="max-w-5xl mx-auto">
          {/* Header with Navigation */}
          <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                icon={Home}
              >
                Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/courses')}
                icon={BookOpen}
              >
                Browse Courses
              </Button>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`/courses/${courseId}`)}
              icon={ArrowLeft}
            >
              Back to Course
            </Button>
          </div>

          {/* Congratulations Message */}
          <div className="text-center mb-8 bg-white rounded-lg p-8 shadow-sm">
            <h1 className="text-3xl font-bold text-neutral-800 mb-2">
              🎉 Congratulations!
            </h1>
            <p className="text-lg text-neutral-600 mb-4">
              You've successfully completed <span className="font-semibold text-primary">{course.title}</span>
            </p>
            <p className="text-neutral-500">
              Download your certificate below and share your achievement!
            </p>
          </div>

          {/* Download Button */}
          <div className="text-center mb-8">
            <Button
              variant="primary"
              size="lg"
              onClick={handleDownload}
              disabled={!pdfUrl || isGeneratingPdf}
            >
              <Download className="w-5 h-5 mr-2" />
              {isGeneratingPdf ? 'Preparing PDF...' : 'Download Certificate'}
            </Button>
          </div>

          {/* Certificate Preview */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-xl font-semibold mb-4 text-center text-neutral-800">
              Certificate Preview
            </h3>
            {isGeneratingPdf && (
              <p className="text-center text-neutral-600">Generating PDF preview...</p>
            )}
            {pdfUrl && !isGeneratingPdf && (
              <iframe
                title="Certificate PDF"
                src={pdfUrl}
                className="w-full h-[750px] border border-neutral-200 rounded-lg bg-white"
              />
            )}
            {!pdfUrl && !isGeneratingPdf && (
              <p className="text-center text-neutral-600">PDF preview will appear here once generated.</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
